import { View, StyleSheet, ViewStyle } from 'react-native'
import { DS } from '../../../design-system'
import { monthDayAndTimeFormatterWithYear } from '../../../utils'
import { PlayerInfoCard } from './PlayerInfoCard'
import { useBreakPointValue } from '../../WebWrapper'

export type PlayerDashboardStats = {
  gamesPlayed: number
  numberOfHands: number
  numberOfHandsPlayed: number
  numberOfHandsWon: number
  allTimesBiggestWin: number
  allTimesBiggestHandWonName: string
  memberSince: number
  lastActive: number
}

export const EMPTY_PLAYER_DASHBOARD_STATS: PlayerDashboardStats = {
  gamesPlayed: 0,
  numberOfHands: 0,
  numberOfHandsPlayed: 0,
  numberOfHandsWon: 0,
  allTimesBiggestWin: 0,
  allTimesBiggestHandWonName: 'N/A',
  memberSince: 0,
  lastActive: 0
}

const ListItem = ({ title, value }: { title: string; value: string }) => {
  return (
    <DS.Frame style={styles.bullet}>
      <DS.Text variant="Body Medium/Regular" color="Brand/Dark Grey">
        {title}
      </DS.Text>
      <DS.Text variant="Body Medium/Medium">{value}</DS.Text>
    </DS.Frame>
  )
}

export const PlayerInformation = ({ stats }: { stats: PlayerDashboardStats }) => {
  const memberSince = stats.memberSince ? monthDayAndTimeFormatterWithYear(new Date(stats.memberSince)) : ''
  const lastActive = stats.lastActive ? monthDayAndTimeFormatterWithYear(new Date(stats.lastActive)) : ''

  const itemsDirection = useBreakPointValue<ViewStyle['flexDirection']>({
    default: 'column',
    lg: 'row'
  })

  const wrap = useBreakPointValue<ViewStyle['flexWrap']>({
    default: 'nowrap',
    lg: 'wrap'
  })

  const bodyGap = useBreakPointValue<ViewStyle['gap']>({
    default: 64,
    lg: 0
  })

  return (
    <View style={styles.container}>
      <DS.Text variant="Heading 2/Medium" textAlign="center">
        Player Information & Statistics
      </DS.Text>
      <DS.Frame style={{ ...styles.body, gap: bodyGap }}>
        <DS.Frame style={{ ...styles.cardsContainer, flexWrap: wrap, flexDirection: itemsDirection }}>
          <PlayerInfoCard
            title="Games Played"
            subtitle="Total games participated"
            metric={stats.gamesPlayed.toString()}
            icon="videogame-asset"
          />
          <PlayerInfoCard
            title="Hands Dealt"
            subtitle="Total hands dealt"
            metric={stats.numberOfHands.toString()}
            icon="back-hand"
          />
          <PlayerInfoCard
            title="Hands Played"
            subtitle="Total hands played"
            metric={stats.numberOfHandsPlayed.toString()}
            icon="back-hand"
          />
          <PlayerInfoCard
            title="Hands Won"
            subtitle="Total hands won"
            metric={stats.numberOfHandsWon.toString()}
            icon="back-hand"
          />
          <PlayerInfoCard
            title="Biggest Win"
            subtitle={stats.allTimesBiggestHandWonName || 'N/A'}
            metric={`${stats.allTimesBiggestWin.toString()} chips`}
            // TODO: Wire allTimesBiggestHandWonCards from Convex when available in dashboard query.
            cards={[]}
            showCards
            icon="attach-money"
          />
        </DS.Frame>
        <DS.Frame style={{ ...styles.list, flexDirection: itemsDirection }}>
          <ListItem title="Member Since:" value={memberSince} />
          <ListItem title="Last Login:" value={lastActive} />
        </DS.Frame>
      </DS.Frame>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: DS.palette['Global/White'],
    borderColor: DS.palette['Brand/Light Grey'],
    borderWidth: 1,
    borderRadius: 12,
    flex: 1,
    alignContent: 'center',
    justifyContent: 'center'
  },
  body: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    flex: 1,
    flexGrow: 1,
    paddingTop: 8
  },
  cardsContainer: {
    justifyContent: 'space-between',
    flex: 1,
    flexGrow: 1,
    gap: 32,
    paddingTop: 16,
    paddingBottom: 32
  },
  playerInfo: {
    flex: 1,
    flexGrow: 1,
    justifyContent: 'center',
    alignContent: 'center'
  },
  list: {
    flex: 1,
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignContent: 'center',
    paddingTop: 16,
    gap: 8
  },
  bullet: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2
  }
})
